import {
	BlockTypeSelect,
	BoldItalicUnderlineToggles,
	CreateLink,
	headingsPlugin,
	imagePlugin,
	linkDialogPlugin,
	linkPlugin,
	listsPlugin,
	ListsToggle,
	markdownShortcutPlugin,
	MDXEditor,
	MDXEditorMethods,
	quotePlugin,
	Separator,
	thematicBreakPlugin,
	toolbarPlugin,
	UndoRedo,
} from '@mdxeditor/editor';
import clsx from 'clsx';
import { useFormik } from 'formik';
import { ArrowLeft, Moon, Sun } from 'lucide-react';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { ShowError } from '../../../../errors/components/ShowError';
import { useGetPostTags } from '../../../feed-tags/tags-table/api/tags.queries';
import {
	useCreatePost,
	useUpdatePost,
	useUploadPostImage,
} from '../../posts-modal/api/posts.mutations';
import { useGetPosts } from '../../posts-table/api/posts.queries';
import { TelegramPublishCard } from './TelegramPublishCard';
import { ImageToolbarButton } from './ImageToolbarButton';
import { NewsPreview } from './NewsPreview';

import '@mdxeditor/editor/style.css';
import '../../posts-modal/ui/mdx-editor-overrides.css';
import '../../posts-modal/ui/article-content.css';

const LANGUAGES = ['ru', 'en', 'hi', 'id', 'zh'] as const;
type Lang = typeof LANGUAGES[number];

const EMPTY_LANGS: Record<Lang, string> = { ru: '', en: '', hi: '', id: '', zh: '' };

// Обязательны только ru и en; остальные языки опциональны.
const REQUIRED_LANGS: ReadonlySet<Lang> = new Set<Lang>(['ru', 'en']);

const buildLangShape = (min: number, max: number | undefined, fieldLabel: string) =>
	Yup.object().shape(
		LANGUAGES.reduce((acc, lang) => {
			const base = max
				? Yup.string().max(max, `Максимум ${max} символов`)
				: Yup.string();
			if (REQUIRED_LANGS.has(lang)) {
				acc[lang] = base
					.min(min, `Минимум ${min} символа`)
					.required(`Введите ${fieldLabel} (${lang.toUpperCase()})`);
			} else {
				acc[lang] = base
					.transform(value => (value === '' ? undefined : value))
					.min(min, `Минимум ${min} символа`)
					.notRequired();
			}
			return acc;
		}, {} as Record<Lang, Yup.StringSchema<any>>)
	);

const PostSchema = Yup.object().shape({
	name: buildLangShape(3, 50, 'название'),
	text: buildLangShape(3, undefined, 'текст'),
	tag_uuid: Yup.string().required('Выберите тег'),
});

// MDXEditor местами сериализует переводы строк как HTML-сущности (&#xA; / &#10;),
// из-за чего блоки «слипаются» и картинки/абзацы не отделяются при рендере.
// Возвращаем реальные переводы строк.
const cleanEditorMarkdown = (md: string): string =>
	md.replace(/&#x0*a;/gi, '\n').replace(/&#0*10;/g, '\n');

const formatDate = (iso?: string): string => {
	const d = iso ? new Date(iso) : new Date();
	return d.toLocaleDateString('ru-RU', {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
	});
};

const PostFormPage: FC = () => {
	const navigate = useNavigate();
	const { uuid } = useParams<{ uuid: string }>();
	const isEdit = Boolean(uuid);

	const [error, setError] = useState();
	const [activeLang, setActiveLang] = useState<Lang>('ru');
	const [previewTheme, setPreviewTheme] = useState<'light' | 'dark'>('light');
	// Меняем ключ редактора, чтобы он перемонтировался и подхватил markdown
	// нужного языка из formik (при переключении языка и при загрузке поста).
	const [contentVersion, setContentVersion] = useState(0);
	const mdxEditorRef = useRef<MDXEditorMethods>(null);

	const { data: tags = [] } = useGetPostTags();
	const { data: posts, isLoading: isPostsLoading } = useGetPosts();
	const post = useMemo(
		() => (isEdit ? posts?.find(p => p.uuid === uuid) : undefined),
		[isEdit, posts, uuid]
	);

	const { mutate: createPost, isLoading: isCreating } = useCreatePost();
	const { mutate: editPost, isLoading: isEditing } = useUpdatePost();
	const {
		mutate: uploadImage,
		mutateAsync: uploadImageAsync,
		isLoading: isUploading,
	} = useUploadPostImage();

	const isSaving = isCreating || isEditing;

	const imageUploadHandler = async (file: File): Promise<string> => {
		const data = await uploadImageAsync(file);
		return `/${data.image_path}`;
	};

	const formik = useFormik({
		initialValues: {
			name: { ...EMPTY_LANGS },
			text: { ...EMPTY_LANGS },
			tag_uuid: '',
			image_path: '',
			publish_to_telegram: false,
		},
		validationSchema: PostSchema,
		enableReinitialize: false,
		onSubmit: values => {
			const onSuccess = () => navigate('/apps/feed/posts');
			const onError = (err: any) => setError(err);
			if (isEdit && uuid) {
				editPost({ uuid, request: values }, { onSuccess, onError });
			} else {
				createPost(values, { onSuccess, onError });
			}
		},
	});

	// Подгрузка данных поста в форму (edit). Бамп contentVersion перемонтирует
	// редактор с загруженным текстом.
	useEffect(() => {
		if (!post) return;
		formik.setValues({
			name: { ...EMPTY_LANGS, ...(post.name || {}) },
			text: { ...EMPTY_LANGS, ...(post.text || {}) },
			tag_uuid: post.tag_uuid || '',
			image_path: post.image_path || '',
			publish_to_telegram: false,
		});
		setActiveLang('ru');
		setContentVersion(v => v + 1);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [post]);

	const handleLangChange = (lang: Lang) => {
		if (lang === activeLang) return;
		// Сохраняем текущий язык во formik.
		const currentText = cleanEditorMarkdown(
			mdxEditorRef.current?.getMarkdown() ?? formik.values.text[activeLang] ?? ''
		);
		const currentName = formik.values.name[activeLang] ?? '';
		formik.setFieldValue(`text.${activeLang}`, currentText);

		// Если у целевого языка ещё пусто — засеваем текстом и названием
		// текущего языка как основой для перевода, чтобы контент не пропадал.
		const targetEmpty =
			!(formik.values.text[lang] ?? '').trim() && !(formik.values.name[lang] ?? '').trim();
		if (targetEmpty) {
			formik.setFieldValue(`text.${lang}`, currentText);
			formik.setFieldValue(`name.${lang}`, currentName);
		}

		// Редактор перемонтируется по ключу (activeLang меняется) и подхватит
		// текст целевого языка из formik.
		setActiveLang(lang);
	};

	const nameError = (formik.errors.name as any)?.[activeLang];
	const nameTouched = (formik.touched.name as any)?.[activeLang];
	const textError = (formik.errors.text as any)?.[activeLang];
	const textTouched = (formik.touched.text as any)?.[activeLang];

	const langHasError = (lang: Lang) =>
		Boolean(
			((formik.touched.name as any)?.[lang] && (formik.errors.name as any)?.[lang]) ||
				((formik.touched.text as any)?.[lang] && (formik.errors.text as any)?.[lang])
		);

	const selectedTag = tags.find(t => t.uuid === formik.values.tag_uuid);
	const tagLabel = selectedTag
		? selectedTag.name?.[activeLang] ||
		  selectedTag.name?.ru ||
		  Object.values(selectedTag.name || {})[0] ||
		  ''
		: '';

	if (isEdit && isPostsLoading) {
		return (
			<div className='d-flex justify-content-center py-20'>
				<div className='spinner-border text-primary' role='status' />
			</div>
		);
	}

	if (isEdit && !post) {
		return (
			<div className='card'>
				<div className='card-body text-center py-15'>
					<h3 className='mb-3'>Пост не найден</h3>
					<button
						className='btn btn-light'
						onClick={() => navigate('/apps/feed/posts')}
					>
						К списку постов
					</button>
				</div>
			</div>
		);
	}

	return (
		<form onSubmit={formik.handleSubmit} noValidate>
			{/* begin::Header */}
			<div className='d-flex align-items-center justify-content-between mb-5'>
				<div className='d-flex align-items-center gap-3'>
					<button
						type='button'
						className='btn btn-icon btn-light btn-sm'
						onClick={() => navigate('/apps/feed/posts')}
						title='Назад к списку'
					>
						<ArrowLeft size={18} />
					</button>
					<h2 className='m-0'>
						{isEdit ? 'Редактирование поста' : 'Новый пост'}
					</h2>
				</div>

				<div className='d-flex gap-3'>
					<button
						type='button'
						className='btn btn-light btn-sm'
						onClick={() => navigate('/apps/feed/posts')}
						disabled={isSaving}
					>
						Отмена
					</button>
					<button
						type='submit'
						className='btn btn-primary btn-sm'
						disabled={isSaving || !formik.isValid}
					>
						{isSaving ? (
							<span>
								Сохранение...{' '}
								<span className='spinner-border spinner-border-sm align-middle ms-1' />
							</span>
						) : isEdit ? (
							'Сохранить'
						) : (
							'Создать'
						)}
					</button>
				</div>
			</div>
			{/* end::Header */}

			<div className='row g-5'>
				{/* begin::Form column */}
				<div className='col-12 col-xl-6'>
					<TelegramPublishCard
						isEdit={isEdit}
						uuid={uuid}
						checked={formik.values.publish_to_telegram}
						onChange={value => formik.setFieldValue('publish_to_telegram', value)}
						nameRu={formik.values.name.ru}
						nameEn={formik.values.name.en}
						imagePath={formik.values.image_path}
					/>

					<div className='card'>
						<div className='card-header'>
							<h3 className='card-title'>Содержание поста</h3>
						</div>
						<div className='card-body'>
							{/* Cover */}
							<div className='mb-7'>
								<label className='fw-bold fs-6 mb-2'>Обложка</label>
								<input
									type='file'
									accept='image/*'
									className='form-control form-control-solid'
									disabled={isUploading}
									onChange={e => {
										const file = e.target.files?.[0];
										if (!file) return;
										uploadImage(file, {
											onSuccess: data =>
												formik.setFieldValue('image_path', data.image_path || ''),
											onError: err => setError(err),
										});
									}}
								/>
								{formik.values.image_path && (
									<div className='mt-2'>
										<img
											src={`/${formik.values.image_path}`}
											alt='Обложка'
											style={{ maxWidth: 160, maxHeight: 120, borderRadius: 8 }}
										/>
									</div>
								)}
							</div>

							{/* Language tabs */}
							<ul className='nav nav-tabs nav-line-tabs nav-stretch fs-6 border-0 mb-5'>
								{LANGUAGES.map(lang => (
									<li key={lang} className='nav-item'>
										<button
											type='button'
											className={clsx('nav-link text-uppercase fw-bold', {
												active: activeLang === lang,
												'text-danger': langHasError(lang),
											})}
											onClick={() => handleLangChange(lang)}
										>
											{lang}
											{REQUIRED_LANGS.has(lang) && (
												<span className='ms-1 text-muted' aria-hidden='true'>
													*
												</span>
											)}
											{langHasError(lang) && (
												<span className='ms-1 text-danger' aria-hidden='true'>
													•
												</span>
											)}
										</button>
									</li>
								))}
							</ul>

							{/* Name */}
							<div className='mb-7'>
								<label
									className={clsx('fw-bold fs-6 mb-2', {
										required: REQUIRED_LANGS.has(activeLang),
									})}
								>
									Название
								</label>
								<input
									type='text'
									placeholder='Название поста'
									{...formik.getFieldProps(`name.${activeLang}`)}
									className={clsx('form-control form-control-solid', {
										'is-invalid': nameTouched && nameError,
									})}
									autoComplete='off'
								/>
								{nameTouched && nameError && (
									<div className='text-danger mt-1'>{nameError}</div>
								)}
							</div>

							{/* Tag */}
							<div className='mb-7'>
								<label className='required fw-bold fs-6 mb-2'>Тег</label>
								<select
									{...formik.getFieldProps('tag_uuid')}
									className={clsx('form-select form-select-solid', {
										'is-invalid': formik.touched.tag_uuid && formik.errors.tag_uuid,
									})}
								>
									<option value=''>— Выберите тег —</option>
									{tags.map(tag => (
										<option key={tag.uuid} value={tag.uuid}>
											{tag.name?.ru || Object.values(tag.name || {})[0] || ''}
										</option>
									))}
								</select>
								{formik.touched.tag_uuid && formik.errors.tag_uuid && (
									<div className='text-danger mt-1'>{formik.errors.tag_uuid}</div>
								)}
							</div>

							{/* Text editor */}
							<div className='mb-3'>
								<label
									className={clsx('fw-bold fs-6 mb-2', {
										required: REQUIRED_LANGS.has(activeLang),
									})}
								>
									Текст поста
								</label>
								<div
									className={clsx('border rounded min-h-300px', {
										'border-danger': textTouched && textError,
									})}
								>
									<MDXEditor
										key={`${activeLang}-${contentVersion}`}
										ref={mdxEditorRef}
										markdown={formik.values.text[activeLang]}
										onChange={md =>
											formik.setFieldValue(`text.${activeLang}`, cleanEditorMarkdown(md))
										}
										onBlur={() => formik.setFieldTouched(`text.${activeLang}`, true)}
										contentEditableClassName='article-content'
										plugins={[
											headingsPlugin(),
											listsPlugin(),
											quotePlugin(),
											thematicBreakPlugin(),
											linkPlugin(),
											linkDialogPlugin(),
											imagePlugin({ imageUploadHandler }),
											markdownShortcutPlugin(),
											toolbarPlugin({
												toolbarContents: () => (
													<>
														<UndoRedo />
														<Separator />
														<BlockTypeSelect />
														<Separator />
														<BoldItalicUnderlineToggles />
														<Separator />
														<ListsToggle />
														<Separator />
														<CreateLink />
														<ImageToolbarButton />
													</>
												),
											}),
										]}
									/>
								</div>
								{textTouched && textError && (
									<div className='text-danger mt-1'>{textError}</div>
								)}
							</div>

							{error && <ShowError error={error} />}
						</div>
					</div>
				</div>
				{/* end::Form column */}

				{/* begin::Preview column */}
				<div className='col-12 col-xl-6'>
					<div>
						<div className='card overflow-hidden'>
							<div className='card-header'>
								<h3 className='card-title'>Превью новости</h3>
								<div className='card-toolbar'>
									<div
										className='btn-group btn-group-sm'
										role='group'
										aria-label='Тема превью'
									>
										<button
											type='button'
											className={clsx(
												'btn btn-sm btn-icon',
												previewTheme === 'light' ? 'btn-primary' : 'btn-light'
											)}
											onClick={() => setPreviewTheme('light')}
											title='Светлая тема'
										>
											<Sun size={16} />
										</button>
										<button
											type='button'
											className={clsx(
												'btn btn-sm btn-icon',
												previewTheme === 'dark' ? 'btn-primary' : 'btn-light'
											)}
											onClick={() => setPreviewTheme('dark')}
											title='Тёмная тема'
										>
											<Moon size={16} />
										</button>
									</div>
								</div>
							</div>
							<div className='card-body p-0'>
								<NewsPreview
									title={formik.values.name[activeLang]}
									tagLabel={tagLabel}
									coverImage={formik.values.image_path}
									date={formatDate(post?.created)}
									markdown={formik.values.text[activeLang]}
									theme={previewTheme}
								/>
							</div>
						</div>
					</div>
				</div>
				{/* end::Preview column */}
			</div>
		</form>
	);
};

export { PostFormPage };
